// src/pages/components/ShopInfoTab.jsx

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const REQUEST_CHANGE_API =
    "https://oppiesfunc.azurewebsites.net/api/submitChangeRequest?code=I2sx0kJ14lN27zbfLJFlZbGDWzCTJzXyPXBbDNkm-MBgAzFu4-VSDQ==";

const GET_SHOP_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopById?code=TxEuYhVjo9rIiMlI2EMr8gRFPPRejKrTeKGMbTXwQ--3AzFuHMjRzQ==";

const UPDATE_PUBLIC_API =
    "https://oppiesfunc.azurewebsites.net/api/updateShopPublicInfo?code=6jBfkkwFCLCP5e5ol_ZR1o3JRedwYOnStjBK3yZUxKAZAzFuUt45vw==";

const EMPTY_FORM = {
    name: "",
    email: "",
    address: "",
    contact: "",
};

function ShopInfoTab({
    shopId,
    userEmail,
}) {
    const [loading, setLoading] = useState(true);

    /*
     * =========================================================
     * SECTION A — PUBLIC INFO
     * =========================================================
     */

    const [
        volunteersNeeded,
        setVolunteersNeeded,
    ] = useState(false);

    const [
        donationsPaused,
        setDonationsPaused,
    ] = useState(false);

    const [
        shopNotes,
        setShopNotes,
    ] = useState("");

    const [
        savedPublicInfo,
        setSavedPublicInfo,
    ] = useState({
        volunteersNeeded: false,
        donationsPaused: false,
        shopNotes: "",
    });

    const [
        savingPublic,
        setSavingPublic,
    ] = useState(false);

    const [
        publicStatus,
        setPublicStatus,
    ] = useState({
        type: "",
        message: "",
    });

    /*
     * =========================================================
     * SECTION B — CHANGE REQUEST
     * =========================================================
     */

    const [form, setForm] =
        useState(EMPTY_FORM);

    const [
        originalForm,
        setOriginalForm,
    ] = useState(EMPTY_FORM);

    const [
        submittingRequest,
        setSubmittingRequest,
    ] = useState(false);

    const [
        requestStatus,
        setRequestStatus,
    ] = useState({
        type: "",
        message: "",
    });

    /*
     * =========================================================
     * LOAD SHOP
     * =========================================================
     */

    useEffect(() => {
        let mounted = true;

        const fetchShop = async () => {
            if (!shopId) {
                setLoading(false);

                setPublicStatus({
                    type: "error",
                    message:
                        "Your shop ID is unavailable. Please sign in again.",
                });

                return;
            }

            try {
                setLoading(true);

                const res = await axios.get(
                    GET_SHOP_API,
                    {
                        params: {
                            shopId,
                        },
                    }
                );

                const shop = res.data || {};

                if (!mounted) return;

                /*
                 * New backend field:
                 * acceptingDonations
                 *
                 * Old compatibility field:
                 * donationsPaused
                 */

                const resolvedDonationsPaused =
                    typeof shop.acceptingDonations ===
                        "boolean"
                        ? !shop.acceptingDonations
                        : !!shop.donationsPaused;

                const publicInfo = {
                    volunteersNeeded:
                        !!shop.volunteersNeeded,

                    donationsPaused:
                        resolvedDonationsPaused,

                    shopNotes: shop.shopNotes
                        ? String(
                            shop.shopNotes
                        ).slice(0, 500)
                        : "",
                };

                const contactInfo = {
                    name:
                        shop.name || "",

                    email:
                        shop.email || "",

                    address:
                        shop.address || "",

                    contact:
                        shop.contact || "",
                };

                setVolunteersNeeded(
                    publicInfo.volunteersNeeded
                );

                setDonationsPaused(
                    publicInfo.donationsPaused
                );

                setShopNotes(
                    publicInfo.shopNotes
                );

                setSavedPublicInfo(
                    publicInfo
                );

                setForm(contactInfo);

                setOriginalForm(
                    contactInfo
                );
            } catch (err) {
                console.error(
                    "❌ Failed to load shop info:",
                    err?.response?.data ||
                    err?.message ||
                    err
                );

                if (!mounted) return;

                setPublicStatus({
                    type: "error",
                    message:
                        "We couldn't load your shop information.",
                });
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchShop();

        return () => {
            mounted = false;
        };
    }, [shopId]);

    /*
     * =========================================================
     * CHANGE DETECTION
     * =========================================================
     */

    const publicHasChanges =
        useMemo(() => {
            return (
                volunteersNeeded !==
                savedPublicInfo
                    .volunteersNeeded ||
                donationsPaused !==
                savedPublicInfo
                    .donationsPaused ||
                shopNotes !==
                savedPublicInfo.shopNotes
            );
        }, [
            volunteersNeeded,
            donationsPaused,
            shopNotes,
            savedPublicInfo,
        ]);

    const requestHasChanges =
        useMemo(() => {
            return Object.keys(
                EMPTY_FORM
            ).some(
                (key) =>
                    String(
                        form[key] || ""
                    ).trim() !==
                    String(
                        originalForm[key] ||
                        ""
                    ).trim()
            );
        }, [form, originalForm]);

    /*
     * =========================================================
     * SAVE PUBLIC INFO
     * =========================================================
     */

    const handleSavePublic = async () => {
        if (!shopId) {
            setPublicStatus({
                type: "error",
                message: "Missing shopId.",
            });
            return;
        }

        if (!userEmail) {
            setPublicStatus({
                type: "error",
                message:
                    "Missing signed-in account email.",
            });
            return;
        }

        if (savingPublic) {
            return;
        }

        try {
            setSavingPublic(true);

            setPublicStatus({
                type: "info",
                message:
                    "Saving public shop information...",
            });

            const payload = {
                shopId,

                // Authenticated Portal account
                userEmail,

                volunteersNeeded,

                // "Donations Needed" ON means the shop
                // is accepting / requesting donations.
                acceptingDonations:
                    !donationsPaused,

                shopNotes:
                    String(
                        shopNotes || ""
                    ).slice(0, 500),
            };

            console.log(
                "📤 updateShopPublicInfo payload:",
                payload
            );

            await axios.post(
                UPDATE_PUBLIC_API,
                payload
            );

            const nextSavedPublicInfo = {
                volunteersNeeded,
                donationsPaused,
                shopNotes:
                    String(
                        shopNotes || ""
                    ).slice(0, 500),
            };

            setSavedPublicInfo(
                nextSavedPublicInfo
            );

            setShopNotes(
                nextSavedPublicInfo.shopNotes
            );

            setPublicStatus({
                type: "success",
                message:
                    "Public shop information saved successfully.",
            });

        } catch (err) {
            const status =
                err?.response?.status;

            console.error(
                "❌ Public info update failed:",
                {
                    status,
                    data:
                        err?.response?.data,
                    message:
                        err?.message,
                }
            );

            setPublicStatus({
                type: "error",
                message:
                    err?.response?.data
                        ?.error ||
                    "Could not save public shop information.",
            });

        } finally {
            setSavingPublic(false);
        }
    };

    /*
     * =========================================================
     * RESET PUBLIC INFO
     * =========================================================
     */

    const resetPublicInfo =
        () => {
            setVolunteersNeeded(
                savedPublicInfo
                    .volunteersNeeded
            );

            setDonationsPaused(
                savedPublicInfo
                    .donationsPaused
            );

            setShopNotes(
                savedPublicInfo
                    .shopNotes
            );

            setPublicStatus({
                type: "info",
                message:
                    "Unsaved public-info changes have been reset.",
            });
        };

    /*
     * =========================================================
     * CONTACT / IDENTITY FORM
     * =========================================================
     */

    const updateForm = (
        field,
        value
    ) => {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));

        if (
            requestStatus.message
        ) {
            setRequestStatus({
                type: "",
                message: "",
            });
        }
    };

    const resetRequestForm =
        () => {
            setForm({
                ...originalForm,
            });

            setRequestStatus({
                type: "info",
                message:
                    "Unsaved changes have been reset.",
            });
        };

    /*
     * =========================================================
     * SUBMIT CHANGE REQUEST
     * =========================================================
     */

    const handleSubmitRequest =
        async () => {
            if (
                !shopId ||
                submittingRequest
            ) {
                return;
            }

            if (!userEmail) {
                setRequestStatus({
                    type: "error",
                    message:
                        "Missing signed-in account email.",
                });

                return;
            }

            if (!requestHasChanges) {
                setRequestStatus({
                    type: "warning",
                    message:
                        "There are no changes to submit.",
                });

                return;
            }

            const cleanedForm = {
                name:
                    String(
                        form.name || ""
                    ).trim(),

                email:
                    String(
                        form.email || ""
                    )
                        .trim()
                        .toLowerCase(),

                address:
                    String(
                        form.address || ""
                    ).trim(),

                contact:
                    String(
                        form.contact || ""
                    ).trim(),
            };

            if (
                !cleanedForm.name ||
                !cleanedForm.email ||
                !cleanedForm.address
            ) {
                setRequestStatus({
                    type: "error",
                    message:
                        "Name, email and address cannot be blank.",
                });

                return;
            }

            try {
                setSubmittingRequest(
                    true
                );

                setRequestStatus({
                    type: "info",
                    message:
                        "Submitting your change request...",
                });

                const payload = {
                    id:
                        Date.now()
                            .toString(),

                    /*
                     * Selected branch being edited
                     */
                    shopId,

                    /*
                     * Authenticated Portal account.
                     * Normal shop = own email.
                     * Manager = Head Office email.
                     */
                    userEmail,

                    ...cleanedForm,
                };

                console.log(
                    "📤 submitChangeRequest payload:",
                    payload
                );

                await axios.post(
                    REQUEST_CHANGE_API,
                    payload
                );

                /*
                 * These values are NOT copied to
                 * originalForm because they have not
                 * yet been approved.
                 */
                setForm(
                    cleanedForm
                );

                setRequestStatus({
                    type: "success",
                    message:
                        "Change request submitted successfully. The OPPIES team will review it before your shop details are changed.",
                });

            } catch (err) {
                console.error(
                    "❌ submitChangeRequest failed:",
                    err?.response?.data ||
                    err?.message ||
                    err
                );

                setRequestStatus({
                    type: "error",
                    message:
                        err?.response?.data
                            ?.error ||
                        "We couldn't submit your change request. Please try again.",
                });

            } finally {
                setSubmittingRequest(
                    false
                );
            }
        };

    /*
     * =========================================================
     * STATUS COMPONENT
     * =========================================================
     */

    const renderStatus = (
        currentStatus
    ) => {
        if (
            !currentStatus.message
        ) {
            return null;
        }

        return (
            <div
                className={`shop-info-status ${currentStatus.type
                        ? `shop-info-status-${currentStatus.type}`
                        : ""
                    }`}
            >
                <div className="shop-info-status-icon">

                    {currentStatus.type ===
                        "success"
                        ? "✓"
                        : currentStatus.type ===
                            "warning" ||
                            currentStatus.type ===
                            "error"
                            ? "!"
                            : "•"}

                </div>

                <span>
                    {currentStatus.message}
                </span>

            </div>
        );
    };

    /*
     * =========================================================
     * LOADING
     * =========================================================
     */

    if (loading) {
        return (
            <>
                <style>
                    {shopInfoStyles}
                </style>

                <div className="shop-info-loading">

                    <div className="shop-info-spinner" />

                    <span>
                        Loading shop information...
                    </span>

                </div>
            </>
        );
    }

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <>
            <style>
                {shopInfoStyles}
            </style>

            <div className="shop-info-page">

                {/* ================================================
            INTRO
        ================================================ */}

                <section className="shop-info-intro">

                    <div className="shop-info-eyebrow">
                        Shop Information
                    </div>

                    <h1>
                        Manage what shoppers see
                    </h1>

                    <p>
                        Update day-to-day information
                        instantly, or request changes
                        to your registered shop
                        identity and contact details.
                    </p>

                </section>

                {/* ================================================
            PUBLIC INFO
        ================================================ */}

                <section className="shop-info-panel">

                    <div className="shop-info-panel-heading">

                        <div>

                            <div className="shop-info-panel-eyebrow">
                                Updates Instantly
                            </div>

                            <h2>
                                Public Information
                            </h2>

                            <p>
                                These details can be
                                updated immediately and
                                shown to OPPIES shoppers
                                without approval.
                            </p>

                        </div>

                        <div className="shop-info-panel-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="22"
                                height="22"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                />

                                <path d="M8 12h8" />

                                <path d="M12 8v8" />

                            </svg>

                        </div>

                    </div>

                    {renderStatus(
                        publicStatus
                    )}

                    <div className="shop-info-toggle-grid">

                        {/* VOLUNTEERS */}

                        <button
                            type="button"
                            className={`shop-info-toggle-card ${volunteersNeeded
                                    ? "shop-info-toggle-card-active"
                                    : ""
                                }`}
                            disabled={
                                savingPublic
                            }
                            onClick={() => {
                                setVolunteersNeeded(
                                    (current) =>
                                        !current
                                );

                                setPublicStatus({
                                    type: "",
                                    message: "",
                                });
                            }}
                        >

                            <div className="shop-info-toggle-copy">

                                <div className="shop-info-toggle-icon">

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />

                                        <circle
                                            cx="9"
                                            cy="7"
                                            r="4"
                                        />

                                        <path d="M19 8v6M22 11h-6" />

                                    </svg>

                                </div>

                                <div>

                                    <strong>
                                        Volunteers Needed
                                    </strong>

                                    <span>
                                        Let shoppers know your
                                        Op Shop is currently
                                        looking for volunteers.
                                    </span>

                                </div>

                            </div>

                            <span
                                className={`shop-info-switch ${volunteersNeeded
                                        ? "shop-info-switch-on"
                                        : ""
                                    }`}
                            >
                                <span />
                            </span>

                        </button>

                        {/* DONATIONS */}

                        <button
                            type="button"
                            className={`shop-info-toggle-card ${donationsPaused
                                    ? "shop-info-toggle-card-warning"
                                    : ""
                                }`}
                            disabled={
                                savingPublic
                            }
                            onClick={() => {
                                setDonationsPaused(
                                    (current) =>
                                        !current
                                );

                                setPublicStatus({
                                    type: "",
                                    message: "",
                                });
                            }}
                        >

                            <div className="shop-info-toggle-copy">

                                <div className="shop-info-toggle-icon">

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M20 12v8H4v-8" />

                                        <path d="M2 7h20v5H2z" />

                                        <path d="M12 22V7" />

                                        <path d="M12 7H7.5A2.5 2.5 0 1 1 10 4.5L12 7Z" />

                                        <path d="M12 7h4.5A2.5 2.5 0 1 0 14 4.5L12 7Z" />

                                    </svg>

                                </div>

                                <div>

                                    <strong>
                                        Donations Paused
                                    </strong>

                                    <span>
                                        Tell shoppers when you
                                        are temporarily unable
                                        to accept donations.
                                    </span>

                                </div>

                            </div>

                            <span
                                className={`shop-info-switch ${donationsPaused
                                        ? "shop-info-switch-on"
                                        : ""
                                    }`}
                            >
                                <span />
                            </span>

                        </button>

                    </div>

                    {/* NOTES */}

                    <div className="shop-info-field">

                        <label htmlFor="shop-notes">
                            Notes for Shoppers

                            <span>
                                Optional
                            </span>
                        </label>

                        <textarea
                            id="shop-notes"
                            rows={5}
                            maxLength={500}
                            disabled={
                                savingPublic
                            }
                            value={shopNotes}
                            placeholder="e.g. Please drop donations at the back door after 10am. Parking is limited on Saturdays."
                            onChange={(event) => {
                                setShopNotes(
                                    event.target.value.slice(
                                        0,
                                        500
                                    )
                                );

                                if (
                                    publicStatus.message
                                ) {
                                    setPublicStatus({
                                        type: "",
                                        message: "",
                                    });
                                }
                            }}
                        />

                        <div className="shop-info-field-footer">

                            <span>
                                Helpful temporary
                                information for shoppers.
                            </span>

                            <strong>
                                {shopNotes.length}/500
                            </strong>

                        </div>

                    </div>

                    {/* PUBLIC ACTIONS */}

                    <div className="shop-info-actions">

                        <div className="shop-info-change-state">

                            {publicHasChanges ? (
                                <>
                                    <span className="shop-info-unsaved-dot" />

                                    Unsaved changes
                                </>
                            ) : (
                                <>
                                    <span className="shop-info-saved-dot" />

                                    Public info up to date
                                </>
                            )}

                        </div>

                        <div className="shop-info-action-buttons">

                            {publicHasChanges ? (
                                <button
                                    type="button"
                                    className="shop-info-secondary-button"
                                    disabled={
                                        savingPublic
                                    }
                                    onClick={
                                        resetPublicInfo
                                    }
                                >
                                    Reset Changes
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="shop-info-primary-button"
                                disabled={
                                    savingPublic ||
                                    !publicHasChanges
                                }
                                onClick={
                                    handleSavePublic
                                }
                            >

                                {savingPublic ? (
                                    <>
                                        <span className="shop-info-button-spinner" />

                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.9"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />

                                            <path d="M17 21v-8H7v8" />

                                            <path d="M7 3v5h8" />

                                        </svg>

                                        Save Public Info
                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </section>

                {/* ================================================
            CHANGE REQUEST
        ================================================ */}

                <section className="shop-info-panel">

                    <div className="shop-info-panel-heading">

                        <div>

                            <div className="shop-info-panel-eyebrow">
                                Approval Required
                            </div>

                            <h2>
                                Registered Shop Details
                            </h2>

                            <p>
                                Changes to your shop name,
                                email, address or contact
                                details are reviewed by
                                the OPPIES team before
                                being applied.
                            </p>

                        </div>

                        <div className="shop-info-panel-icon shop-info-panel-icon-cream">

                            <svg
                                viewBox="0 0 24 24"
                                width="22"
                                height="22"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6Z" />

                                <path d="m9 12 2 2 4-4" />

                            </svg>

                        </div>

                    </div>

                    <div className="shop-info-approval-note">

                        <div className="shop-info-approval-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="17"
                                height="17"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                />

                                <path d="M12 11v5" />

                                <path d="M12 8h.01" />

                            </svg>

                        </div>

                        <div>

                            <strong>
                                Why approval?
                            </strong>

                            <span>
                                These details identify your
                                verified Op Shop within
                                OPPIES, so changes are
                                reviewed before replacing
                                your current information.
                            </span>

                        </div>

                    </div>

                    {renderStatus(
                        requestStatus
                    )}

                    <div className="shop-info-form-grid">

                        <div className="shop-info-field">

                            <label htmlFor="shop-name">
                                Shop Name
                            </label>

                            <input
                                id="shop-name"
                                type="text"
                                maxLength={150}
                                disabled={
                                    submittingRequest
                                }
                                value={form.name}
                                placeholder="Shop name"
                                onChange={(event) =>
                                    updateForm(
                                        "name",
                                        event.target.value
                                    )
                                }
                            />

                            <div className="shop-info-current-value">
                                Current verified value:

                                <strong>
                                    {originalForm.name ||
                                        "Not set"}
                                </strong>
                            </div>

                        </div>

                        <div className="shop-info-field">

                            <label htmlFor="shop-email">
                                Shop Email
                            </label>

                            <input
                                id="shop-email"
                                type="email"
                                maxLength={200}
                                disabled={
                                    submittingRequest
                                }
                                value={form.email}
                                placeholder="Shop email"
                                onChange={(event) =>
                                    updateForm(
                                        "email",
                                        event.target.value
                                    )
                                }
                            />

                            <div className="shop-info-current-value">
                                Current verified value:

                                <strong>
                                    {originalForm.email ||
                                        "Not set"}
                                </strong>
                            </div>

                        </div>

                        <div className="shop-info-field shop-info-field-full">

                            <label htmlFor="shop-address">
                                Shop Address
                            </label>

                            <input
                                id="shop-address"
                                type="text"
                                maxLength={250}
                                disabled={
                                    submittingRequest
                                }
                                value={form.address}
                                placeholder="Shop address"
                                onChange={(event) =>
                                    updateForm(
                                        "address",
                                        event.target.value
                                    )
                                }
                            />

                            <div className="shop-info-current-value">
                                Current verified value:

                                <strong>
                                    {originalForm.address ||
                                        "Not set"}
                                </strong>
                            </div>

                        </div>

                        <div className="shop-info-field shop-info-field-full">

                            <label htmlFor="shop-contact">
                                Contact Number

                                <span>
                                    Optional
                                </span>
                            </label>

                            <input
                                id="shop-contact"
                                type="tel"
                                maxLength={80}
                                disabled={
                                    submittingRequest
                                }
                                value={form.contact}
                                placeholder="Shop contact number"
                                onChange={(event) =>
                                    updateForm(
                                        "contact",
                                        event.target.value
                                    )
                                }
                            />

                            <div className="shop-info-current-value">
                                Current verified value:

                                <strong>
                                    {originalForm.contact ||
                                        "Not set"}
                                </strong>
                            </div>

                        </div>

                    </div>

                    {/* REQUEST ACTIONS */}

                    <div className="shop-info-actions">

                        <div className="shop-info-change-state">

                            {requestHasChanges ? (
                                <>
                                    <span className="shop-info-unsaved-dot" />

                                    Changes ready to submit
                                </>
                            ) : (
                                <>
                                    <span className="shop-info-saved-dot" />

                                    No changes requested
                                </>
                            )}

                        </div>

                        <div className="shop-info-action-buttons">

                            {requestHasChanges ? (
                                <button
                                    type="button"
                                    className="shop-info-secondary-button"
                                    disabled={
                                        submittingRequest
                                    }
                                    onClick={
                                        resetRequestForm
                                    }
                                >
                                    Reset Changes
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="shop-info-primary-button"
                                disabled={
                                    submittingRequest ||
                                    !requestHasChanges
                                }
                                onClick={
                                    handleSubmitRequest
                                }
                            >

                                {submittingRequest ? (
                                    <>
                                        <span className="shop-info-button-spinner" />

                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="16"
                                            height="16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.9"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="m22 2-7 20-4-9-9-4Z" />

                                            <path d="M22 2 11 13" />

                                        </svg>

                                        Submit Change Request
                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </section>

            </div>
        </>
    );
}

const shopInfoStyles = `

  .shop-info-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  .shop-info-intro {
    margin-bottom: 26px;
  }

  .shop-info-eyebrow,
  .shop-info-panel-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .shop-info-intro h1 {
    margin: 7px 0;
    color: #17211c;
    font-size: clamp(26px, 3vw, 36px);
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .shop-info-intro p {
    max-width: 690px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  .shop-info-panel {
    margin-bottom: 20px;
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px
      rgba(15, 23, 42, 0.035);
  }

  .shop-info-panel-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 20px;
  }

  .shop-info-panel-heading h2 {
    margin: 4px 0 5px;
    color: #17211c;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .shop-info-panel-heading p {
    max-width: 620px;
    margin: 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .shop-info-panel-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    flex: 0 0 auto;
    border-radius: 13px;
    background: #ecfdf5;
    color: #047857;
  }

  .shop-info-panel-icon-cream {
    background: #fbf7ed;
  }

  .shop-info-status {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 17px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 13px;
    background: #ffffff;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
  }

  .shop-info-status-icon {
    display: grid;
    place-items: center;
    width: 25px;
    height: 25px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #f1f5f9;
    font-size: 12px;
    font-weight: 900;
  }

  .shop-info-status-success {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #047857;
  }

  .shop-info-status-success
  .shop-info-status-icon {
    background: #dcfce7;
  }

  .shop-info-status-warning {
    border-color: #fde68a;
    background: #fffbeb;
    color: #92400e;
  }

  .shop-info-status-warning
  .shop-info-status-icon {
    background: #fef3c7;
  }

  .shop-info-status-error {
    border-color: #fecaca;
    background: #fef2f2;
    color: #b91c1c;
  }

  .shop-info-status-error
  .shop-info-status-icon {
    background: #fee2e2;
  }

  .shop-info-status-info {
    border-color: #d1fae5;
    background: #f0fdf4;
    color: #047857;
  }

  .shop-info-toggle-grid {
    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
    gap: 13px;
    margin-bottom: 17px;
  }

  .shop-info-toggle-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    min-height: 98px;
    padding: 15px;
    border: 1px solid #e7e5df;
    border-radius: 15px;
    background: #fbf7ed;
    color: inherit;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
  }

  .shop-info-toggle-card:hover:not(:disabled) {
    border-color: #cde8dd;
  }

  .shop-info-toggle-card-active {
    border-color: #a7f3d0;
    background: #f0fdf4;
  }

  .shop-info-toggle-card-warning {
    border-color: #fde68a;
    background: #fffbeb;
  }

  .shop-info-toggle-card:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .shop-info-toggle-copy {
    display: flex;
    min-width: 0;
    align-items: flex-start;
    gap: 11px;
  }

  .shop-info-toggle-icon {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    flex: 0 0 auto;
    border-radius: 11px;
    background: #ffffff;
    color: #047857;
  }

  .shop-info-toggle-copy strong {
    display: block;
    margin-bottom: 3px;
    color: #334155;
    font-size: 11px;
  }

  .shop-info-toggle-copy span {
    display: block;
    max-width: 280px;
    color: #64748b;
    font-size: 9px;
    line-height: 1.5;
  }

  .shop-info-switch {
    position: relative;
    width: 37px;
    height: 21px;
    flex: 0 0 auto;
    border-radius: 999px;
    background: #cbd5e1;
    transition: background 0.18s ease;
  }

  .shop-info-switch span {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow:
      0 1px 4px
      rgba(0, 0, 0, 0.18);
    transition: transform 0.18s ease;
  }

  .shop-info-switch-on {
    background: #047857;
  }

  .shop-info-switch-on span {
    transform: translateX(16px);
  }

  .shop-info-form-grid {
    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
    gap: 15px;
  }

  .shop-info-field {
    min-width: 0;
    margin-bottom: 14px;
  }

  .shop-info-field-full {
    grid-column: 1 / -1;
  }

  .shop-info-field label {
    display: block;
    margin-bottom: 6px;
    color: #334155;
    font-size: 11px;
    font-weight: 800;
  }

  .shop-info-field label span {
    margin-left: 6px;
    color: #94a3b8;
    font-size: 9px;
    font-weight: 700;
  }

  .shop-info-field input,
  .shop-info-field textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #d8ddd9;
    border-radius: 11px;
    outline: none;
    background: #ffffff;
    color: #1f2937;
    font-family: inherit;
    font-size: 11px;
  }

  .shop-info-field input {
    min-height: 42px;
    padding: 10px 12px;
  }

  .shop-info-field textarea {
    min-height: 112px;
    padding: 11px 12px;
    resize: vertical;
    line-height: 1.55;
  }

  .shop-info-field input:focus,
  .shop-info-field textarea:focus {
    border-color: #34a27a;
    box-shadow:
      0 0 0 3px
      rgba(4, 120, 87, 0.08);
  }

  .shop-info-field input:disabled,
  .shop-info-field textarea:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .shop-info-field-footer {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 5px;
    color: #94a3b8;
    font-size: 9px;
  }

  .shop-info-field-footer strong {
    flex: 0 0 auto;
    color: #64748b;
  }

  .shop-info-current-value {
    margin-top: 5px;
    color: #94a3b8;
    font-size: 8.5px;
    line-height: 1.45;
  }

  .shop-info-current-value strong {
    margin-left: 4px;
    color: #64748b;
    font-weight: 700;
  }

  .shop-info-approval-note {
    display: flex;
    gap: 10px;
    margin-bottom: 17px;
    padding: 12px 13px;
    border: 1px solid #e7e5df;
    border-radius: 12px;
    background: #fbf7ed;
  }

  .shop-info-approval-icon {
    display: grid;
    place-items: center;
    width: 31px;
    height: 31px;
    flex: 0 0 auto;
    border-radius: 9px;
    background: #ffffff;
    color: #047857;
  }

  .shop-info-approval-note strong {
    display: block;
    margin-bottom: 2px;
    color: #475569;
    font-size: 10px;
  }

  .shop-info-approval-note span {
    display: block;
    max-width: 650px;
    color: #64748b;
    font-size: 9px;
    line-height: 1.5;
  }

  .shop-info-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 4px;
    padding-top: 17px;
    border-top: 1px solid #eef2f0;
  }

  .shop-info-change-state {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #64748b;
    font-size: 9px;
    font-weight: 700;
  }

  .shop-info-unsaved-dot,
  .shop-info-saved-dot {
    width: 7px;
    height: 7px;
    flex: 0 0 auto;
    border-radius: 50%;
  }

  .shop-info-unsaved-dot {
    background: #f59e0b;
  }

  .shop-info-saved-dot {
    background: #10b981;
  }

  .shop-info-action-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .shop-info-primary-button,
  .shop-info-secondary-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 39px;
    padding: 9px 13px;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
  }

  .shop-info-primary-button {
    border: none;
    background: #047857;
    color: #ffffff;
    box-shadow:
      0 7px 18px
      rgba(4, 120, 87, 0.16);
  }

  .shop-info-primary-button:hover:not(:disabled) {
    background: #036b4f;
  }

  .shop-info-secondary-button {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #64748b;
  }

  .shop-info-secondary-button:hover:not(:disabled) {
    background: #f8fafc;
  }

  .shop-info-primary-button:disabled,
  .shop-info-secondary-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .shop-info-button-spinner {
    width: 11px;
    height: 11px;
    border: 2px solid
      rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation:
      shop-info-spin
      0.7s linear infinite;
  }

  .shop-info-loading {
    display: flex;
    min-height: 320px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 11px;
    color: #94a3b8;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
    font-size: 11px;
  }

  .shop-info-spinner {
    width: 29px;
    height: 29px;
    border: 3px solid #d1fae5;
    border-top-color: #047857;
    border-radius: 50%;
    animation:
      shop-info-spin
      0.8s linear infinite;
  }

  @keyframes shop-info-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 800px) {
    .shop-info-toggle-grid,
    .shop-info-form-grid {
      grid-template-columns: 1fr;
    }

    .shop-info-field-full {
      grid-column: 1;
    }
  }

  @media (max-width: 560px) {
    .shop-info-panel {
      padding: 17px;
      border-radius: 17px;
    }

    .shop-info-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .shop-info-action-buttons {
      width: 100%;
    }

    .shop-info-primary-button,
    .shop-info-secondary-button {
      flex: 1;
    }
  }

  @media (max-width: 420px) {
    .shop-info-toggle-card {
      align-items: flex-start;
    }

    .shop-info-action-buttons {
      flex-direction: column;
    }

    .shop-info-primary-button,
    .shop-info-secondary-button {
      width: 100%;
    }
  }

`;

export default ShopInfoTab;